import { cn } from "@/lib/utils";
import { Cell } from "@tanstack/react-table";
import { TableCell } from "@/components/ui/table";
import { memo, useMemo } from "react";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { SelectRowCell } from "@/components/data-table/components/SelectRowCell";
import PeriodCheckbox from "./PeriodCheckbox";

interface PeriodsTableCellProps<TData extends { id: string }> {
  cell: Cell<TData, unknown>;
  isBusy: boolean;
}

const PeriodsTableCell = <TData extends { id: string }>({
  cell,
  isBusy,
}: PeriodsTableCellProps<TData>) => {
  const { can_update } = useModulePermissions();
  const style = useMemo(
    () => ({
      width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
    }),
    [cell.column.id],
  );
  return (
    <TableCell
      key={cell.id}
      className={cn("flex justify-center items-center p-0 grow-0 shrink-0", {
        "bg-slate-200 sticky !left-0": cell.column.getIsPinned() === "left",
        "z-10": cell.column.id === "selectStore",
      })}
      style={style}
    >
      <span className={cn("flex items-center justify-center p-0 h-8")}>
        {cell.column.id === "selectStore" ? (
          <SelectRowCell
            id={cell.row.original.id}
            disabled={isBusy || !can_update}
          />
        ) : (
          <PeriodCheckbox
            storeId={cell.row.original.id}
            periodId={cell.column.id}
            isBusy={isBusy}
          />
        )}
      </span>
    </TableCell>
  );
};

export default memo(PeriodsTableCell) as <TData extends { id: string }>(
  props: PeriodsTableCellProps<TData>,
) => JSX.Element;
